import type {
  Position,
  NodeState,
  NodeRendererContext,
  DiagramLabelPosition,
  DiagramNodeData
} from './diagram.types';
import { useResizeObserver } from '#hooks/UNSAFE_useResizeObserver';
import { useCallback, useRef } from 'preact/hooks';
import { getItemAriaLabel } from './utils/diagramUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { buttonStyle, itemStyles, layerStyles } from './themes/DiagramStyles.css';
import { ComponentChildren } from 'preact';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import { DiagramLabel } from './DiagramLabel';
import { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { EmbeddedIconButton } from '#PRIVATE_EmbeddedIconButton';
import { Plus } from '#UNSAFE_RedwoodIcons/Plus';
import { Minus } from '#UNSAFE_RedwoodIcons/Minus';

/**
 * Props for node component
 */
type NodeProps<K1, K2, D1> = {
  id: K1;
  position?: Position;
  state: NodeState;
  isDimmed?: boolean;
  activeId?: string;
  accessibleLabel?: string;
  data: D1;
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  onNodeSizeChanged: (width: number, height: number, id: K1) => void;
  supportsSelection: boolean;
  content?: { content: ComponentChildren; width: number; height: number };
  labelPosition?: DiagramLabelPosition;
  labelDimensions?: Map<K1 | K2, TextDimensions>;
};

export const DiagramNodeElement = <K1, K2, D1 extends DiagramNodeData<K1, D1>>({
  id,
  state,
  position,
  activeId,
  nodeRenderer,
  onNodeSizeChanged,
  data,
  accessibleLabel,
  isDimmed,
  supportsSelection,
  content,
  labelPosition,
  labelDimensions
}: NodeProps<K1, K2, D1>) => {
  const { nodeStyles, dimmedItemStyle } = itemStyles;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ref = useRef<HTMLDivElement>(null);
  // TODO JET-62472 change to one resize observer function
  const optionsRef = useRef({ box: 'border-box' as const });

  useResizeObserver(
    ref,
    useCallback(
      (entry: ResizeObserverEntry) => {
        const boxSize = entry.borderBoxSize;
        const width = boxSize[0].inlineSize;
        const height = boxSize[0].blockSize;
        onNodeSizeChanged(width, height, id);
      },
      [id, onNodeSizeChanged]
    ),
    optionsRef.current
  );
  const nodePos = position ? position : { x: 0, y: 0 };
  const { direction } = useUser();
  const hasLabel = data.label && labelPosition && labelDimensions;
  const expander = (
    <div data-oj-private-button={id} class={buttonStyle}>
      <EmbeddedIconButton aria-hidden={true} size="xs">
        {content ? <Minus /> : <Plus />}
      </EmbeddedIconButton>
    </div>
  );
  const context = {
    state: state,
    data: data,
    childContext: content,
    expander: expander
  };
  return (
    <div
      ref={ref}
      key={id}
      class={nodeStyles}
      data-oj-private-node={id}
      role={content ? 'group' : "img"}
      aria-label={getItemAriaLabel(
        translations,
        supportsSelection,
        {
          isSelected: state.isSelected,
          isExpanded: content !== undefined,
          isCollapsed: (content === undefined) && (data.nodes !== undefined)
        },
        accessibleLabel
      )}
      style={{
        top: nodePos.y,
        left: nodePos.x
      }}
      id={state.isFocused || state.isHovered ? activeId : undefined}>
      <div class={classNames([isDimmed ? dimmedItemStyle : undefined])}>
        {nodeRenderer(context)}
      </div>
      {hasLabel ? (
        <div class={layerStyles}>
          <DiagramLabel
            styles={data.labelStyle}
            text={data.label!}
            position={labelPosition!}
            dimensions={labelDimensions.get(id)!}
            isRTL={direction === 'rtl'}
          />
        </div>
      ) : undefined}
    </div>
  );
};
